import { type FC, useCallback, useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Label } from "@/shared/components/ui/label";
import { UploadZone } from "@/shared/components/ui/upload-zone";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

import { usePerson } from "@/shared/hooks/useAppContext";
import { DocumentType } from "@/shared/types/documentTypes";
import { uploadDocument } from "@/shared/utils/uploadDocument";
import type { FlowAnswers, FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

interface MilitaryDetailsData {
  branch: string;
  startYear: string;
  endYear: string;
}

const ANSWER_KEY = "military_details";

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1920 + 1 }, (_, i) => String(currentYear - i));

const BRANCHES = [
  "Army",
  "Navy",
  "Air Force",
  "Marine Corps",
  "Coast Guard",
  "Space Force",
  "National Guard",
] as const;

function parseDetails(raw: string | string[] | undefined): MilitaryDetailsData {
  if (typeof raw === "string" && raw.length > 0) {
    try {
      return JSON.parse(raw) as MilitaryDetailsData;
    } catch (err) {
      sentryUtils.captureError(err, "MilitaryDetails parse failed", undefined, "warning");
      // fall through
    }
  }
  return { branch: "", startYear: "", endYear: "" };
}

function serializeDetails(data: MilitaryDetailsData): string {
  return JSON.stringify(data);
}

export function validateMilitaryDetails(answers: FlowAnswers): Record<string, string> {
  const details = parseDetails(answers[ANSWER_KEY]);
  if (details.startYear && details.endYear && Number(details.startYear) > Number(details.endYear)) {
    return {
      [ANSWER_KEY]: "Start year cannot be after end year.",
    };
  }
  return {};
}

export const MilitaryDetails: FC<FlowCustomComponentProps> = ({
  onComplete,
  answers,
  setAnswer,
  errors,
}) => {
  const person = usePerson();
  const estateId = person?.estate_id;

  const [details, setDetails] = useState<MilitaryDetailsData>(() =>
    parseDetails(answers[ANSWER_KEY]),
  );
  const [dd214File, setDd214File] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const syncToAnswers = useCallback(
    (updated: MilitaryDetailsData) => {
      setAnswer(ANSWER_KEY, serializeDetails(updated));
    },
    [setAnswer],
  );

  useEffect(() => {
    if (!answers[ANSWER_KEY]) {
      syncToAnswers(details);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const raw = answers[ANSWER_KEY];
    if (typeof raw === "string" && raw !== serializeDetails(details)) {
      setDetails(parseDetails(raw));
    }
  }, [answers[ANSWER_KEY]]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (field: keyof MilitaryDetailsData, value: string) => {
    setDetails((prev) => {
      const updated = { ...prev, [field]: value };
      syncToAnswers(updated);
      return updated;
    });
  };

  const handleContinue = async () => {
    if (dd214File && estateId) {
      setIsUploading(true);
      setUploadError(null);
      try {
        await uploadDocument(dd214File, DocumentType.DISCHARGE_PAPERS, estateId);
      } catch (err) {
        setUploadError("Failed to upload document. Please try again.");
        setIsUploading(false);
        sentryUtils.captureError(err, "DD214 upload failed", { estateId });
        return;
      }
      setIsUploading(false);
    }
    onComplete();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6 rounded-lg border border-primary bg-white p-6">
        <div>
          <Label className="mb-2 inline-block">
            Branch of service <span className="text-tertiary">(optional)</span>
          </Label>
          <Select value={details.branch} onValueChange={(val) => updateField("branch", val)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select branch" />
            </SelectTrigger>
            <SelectContent>
              {BRANCHES.map((branch) => (
                <SelectItem key={branch} value={branch}>
                  {branch}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-2 inline-block">
            Years of service <span className="text-tertiary">(optional)</span>
          </Label>
          <div className="flex items-center gap-3">
            <Select
              value={details.startYear}
              onValueChange={(val) => updateField("startYear", val)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Start year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={details.endYear} onValueChange={(val) => updateField("endYear", val)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="End year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <FieldError message={errors?.[ANSWER_KEY]} className="mt-1.5" />
        </div>

        <div>
          <Label className="mb-2 inline-block">
            Do you have their DD-214 (discharge papers)?{" "}
            <span className="text-tertiary">(optional)</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipInfoTrigger aria-label={WHY_WE_ASK_TITLE} className="ml-1 align-middle" />
                <TooltipContent
                  title={WHY_WE_ASK_TITLE}
                >{`The DD-214 is ${person?.first_name || "your loved one"}'s discharge paper, and the VA asks for it before paying burial or survivor benefits. If you can't find it, you can request a copy from the National Archives.`}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Label>
          <UploadZone
            file={dd214File}
            onFileSelect={setDd214File}
            onFileRemove={() => setDd214File(null)}
          />
        </div>
      </div>

      <FieldError message={uploadError} />

      <Button
        variant="brand"
        rounded
        className="w-full"
        disabled={isUploading}
        onClick={handleContinue}
      >
        {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
      </Button>
    </div>
  );
};
